import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import SectionHeading from '../../components/SectionHeading';
import ComingSoon from '../../components/ComingSoon';
import {
  fetchCrossServerSummary,
  formatHubInstant,
  hubUrl,
  HUB_QUICK_LINKS,
  HUB_SITE_URL,
} from '../../service/hubApi';
import { useDisplayTimezone } from '../../service/localTime';
import { ReactComponent as ExternalIcon } from '../../assets/img/arrow-up-right.svg';
import HubLinkIcon from './icons';
import {
  StyledPage,
  StyledHero,
  StyledHeroCopy,
  StyledHeroLink,
  StyledGrid,
  StyledColumn,
  StyledCard,
  StyledList,
  StyledRowLink,
  StyledRowMain,
  StyledLogo,
  StyledLogoStack,
  StyledNameBlock,
  StyledName,
  StyledHint,
  StyledMeta,
  StyledHeadingLink,
  StyledEmpty,
  StyledSkeleton,
  StyledSkeletonLine,
  StyledLinkGrid,
  StyledQuickLink,
  StyledQuickCopy,
  StyledQuickLabel,
  StyledQuickHint,
  StyledWinnersGrid,
  StyledWinnerCard,
  StyledWinnerBrand,
  StyledWinnerClub,
  StyledClubMark,
  StyledClubFallback,
  StyledWinnerMeta,
  StyledWinnerTeam,
  StyledWinnerWho,
} from './styled';

const LoadingCard = () => (
  <StyledCard>
    <StyledSkeleton>
      <StyledSkeletonLine $width="70%" />
      <StyledSkeletonLine $width="54%" />
      <StyledSkeletonLine $width="62%" />
    </StyledSkeleton>
  </StyledCard>
);

const FailedCard = ({ text }) => (
  <ComingSoon>
    <StyledCard>
      <StyledEmpty>{text}</StyledEmpty>
    </StyledCard>
  </ComingSoon>
);

const applicationMeta = (item, zone) => {
  if (item.open) {
    const closes = formatHubInstant(item.closesAt, zone);
    return closes ? `Open · closes ${closes}` : 'Open';
  }
  const opens = formatHubInstant(item.opensAt, zone);
  return opens ? `Closed · opens ${opens}` : 'Closed';
};

const CompLogo = ({ item, size = 36 }) => {
  const theme = useTheme();
  const src = theme.isDark ? item?.logoDark : item?.logoLight;
  if (!src) return null;
  return <StyledLogo src={src} alt="" $size={size} />;
};

const clubInitials = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

const WinnerTile = ({ item }) => (
  <StyledWinnerCard href={item.href} target="_blank" rel="noreferrer noopener">
    <StyledWinnerBrand>
      <CompLogo item={item} size={32} />
      <StyledNameBlock>
        <StyledName>{item.name}</StyledName>
        <StyledWinnerMeta>{item.seasonLabel}</StyledWinnerMeta>
      </StyledNameBlock>
    </StyledWinnerBrand>
    <StyledWinnerClub>
      <StyledClubMark>
        {item.teamLogo ? (
          <img src={item.teamLogo} alt="" />
        ) : (
          <StyledClubFallback>{clubInitials(item.team)}</StyledClubFallback>
        )}
      </StyledClubMark>
      <span>
        <StyledWinnerTeam>{item.team}</StyledWinnerTeam>
        <StyledWinnerWho>
          {[item.username, item.server].filter(Boolean).join(' · ')}
        </StyledWinnerWho>
      </span>
    </StyledWinnerClub>
  </StyledWinnerCard>
);

export default function CrossServer() {
  const zone = useDisplayTimezone();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    seasonLabel: null,
    competitions: null,
    applications: null,
    winners: null,
    competitionsFailed: false,
    applicationsFailed: false,
    winnersFailed: false,
  });

  useEffect(() => {
    let cancelled = false;
    fetchCrossServerSummary()
      .then((next) => {
        if (!cancelled) setSummary(next);
      })
      .catch(() => {
        if (!cancelled) {
          setSummary({
            seasonLabel: null,
            competitions: null,
            applications: null,
            winners: null,
            competitionsFailed: true,
            applicationsFailed: true,
            winnersFailed: true,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const heroLine = summary.seasonLabel
    ? `${summary.seasonLabel} · community cups across every server`
    : 'Community cups across every server';

  return (
    <StyledPage>
      <StyledHero>
        <StyledHeroCopy>
          <h1>Cross server</h1>
          <p>{heroLine}</p>
        </StyledHeroCopy>
        <StyledHeroLink
          href={HUB_SITE_URL}
          target="_blank"
          rel="noreferrer noopener"
        >
          Open TOL Hub
          <ExternalIcon />
        </StyledHeroLink>
      </StyledHero>
      <SectionHeading
        title="Last season"
        action={
          <StyledHeadingLink
            href={hubUrl('/archive')}
            target="_blank"
            rel="noreferrer noopener"
          >
            Archive
            <ExternalIcon />
          </StyledHeadingLink>
        }
      />
      {loading ? (
        <StyledWinnersGrid>
          <LoadingCard />
          <LoadingCard />
          <LoadingCard />
          <LoadingCard />
        </StyledWinnersGrid>
      ) : summary.winnersFailed ? (
        <FailedCard text="Last season’s winners will show here." />
      ) : summary.winners.length ? (
        <StyledWinnersGrid>
          {summary.winners.map((item) => (
            <WinnerTile key={item.key} item={item} />
          ))}
        </StyledWinnersGrid>
      ) : (
        <StyledCard>
          <StyledEmpty>No last-season winners listed yet.</StyledEmpty>
        </StyledCard>
      )}
      <StyledGrid>
        <StyledColumn>
          <SectionHeading
            title="Current competitions"
            action={
              <StyledHeadingLink
                href={hubUrl('/tournaments')}
                target="_blank"
                rel="noreferrer noopener"
              >
                All cups
                <ExternalIcon />
              </StyledHeadingLink>
            }
          />
          {loading ? (
            <LoadingCard />
          ) : summary.competitionsFailed ? (
            <FailedCard text="Live Hub facts will show here." />
          ) : (
            <StyledCard>
              {summary.competitions.length ? (
                <StyledList>
                  {summary.competitions.map((item) => (
                    <StyledRowLink
                      key={item.key}
                      href={item.href}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <StyledRowMain>
                        <CompLogo item={item} size={36} />
                        <StyledName>{item.name}</StyledName>
                      </StyledRowMain>
                      <ExternalIcon />
                    </StyledRowLink>
                  ))}
                </StyledList>
              ) : (
                <StyledEmpty>No current competitions listed.</StyledEmpty>
              )}
            </StyledCard>
          )}
        </StyledColumn>
        <StyledColumn>
          <SectionHeading
            title="Applications"
            action={
              <StyledHeadingLink
                href={hubUrl('/tournaments/applications')}
                target="_blank"
                rel="noreferrer noopener"
              >
                Apply
                <ExternalIcon />
              </StyledHeadingLink>
            }
          />
          {loading ? (
            <LoadingCard />
          ) : summary.applicationsFailed ? (
            <FailedCard text="Live Hub facts will show here." />
          ) : (
            <StyledCard>
              {summary.applications.length ? (
                <StyledList>
                  {summary.applications.map((item) => (
                    <StyledRowLink
                      key={item.key}
                      href={item.href}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      <StyledRowMain>
                        {item.logos?.length ? (
                          <StyledLogoStack>
                            {item.logos.map((logo) => (
                              <CompLogo key={logo.key} item={logo} size={28} />
                            ))}
                          </StyledLogoStack>
                        ) : null}
                        <StyledNameBlock>
                          <StyledName>{item.name}</StyledName>
                          <StyledHint>{applicationMeta(item, zone)}</StyledHint>
                        </StyledNameBlock>
                      </StyledRowMain>
                      <StyledMeta $open={item.open}>
                        {item.open ? 'Open' : 'Closed'}
                      </StyledMeta>
                    </StyledRowLink>
                  ))}
                </StyledList>
              ) : (
                <StyledEmpty>No application windows listed.</StyledEmpty>
              )}
            </StyledCard>
          )}
        </StyledColumn>
      </StyledGrid>
      <SectionHeading title="On TOL Hub" />
      <StyledLinkGrid>
        {HUB_QUICK_LINKS.map((item) => (
          <StyledQuickLink
            key={item.href}
            href={hubUrl(item.href)}
            target="_blank"
            rel="noreferrer noopener"
          >
            <HubLinkIcon name={item.icon} />
            <StyledQuickCopy>
              <StyledQuickLabel>{item.label}</StyledQuickLabel>
              <StyledQuickHint>{item.hint}</StyledQuickHint>
            </StyledQuickCopy>
          </StyledQuickLink>
        ))}
      </StyledLinkGrid>
    </StyledPage>
  );
}
